/**
 * useApplicationRecorder (navigateur) — la caméra frontale qui enregistre une réponse vidéo de la
 * candidature, port d'`tutor/application/motivation/VideoRecordingComponent.web.js` :
 *
 *  - l'accès à la caméra et au micro vérifié à l'ouverture (`getUserMedia`, le flux de test aussitôt
 *    rendu) ; refusé, l'écran le dit et « Autoriser » redemande (l'ancien ne disait rien) ;
 *  - l'enregistrement part 2 s après l'accès, par `MediaRecorder` sur le flux de `react-webcam`
 *    (`video/webm`, sinon `video/mp4`), en miroir et sans retour du son ;
 *  - à la fin du temps, il s'arrête ; ses morceaux font la vidéo (`blob:`), posée dans
 *    `state.camera.videoRecord` (`{ uri }`), que la question envoie. Une vidéo d'un envoi d'avant,
 *    restée dans le store, ne l'arrête pas (l'ancien revenait aussitôt en arrière, en boucle).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { View } from "react-native"
import { useDispatch } from "react-redux"
import Webcam from "react-webcam"

import { setVideoRecord } from "../../../../data/redux/actions/camera"
import {
    type ApplicationRecorder,
    type ApplicationRecorderArgs,
    RECORDING_START_DELAY_MS,
    recorderPhase,
} from "./applicationRecorder"

function recorderMimeType(): string {
    return typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "video/mp4"
}

export function useApplicationRecorder(args: ApplicationRecorderArgs): ApplicationRecorder {
    const { secondsLeft } = args
    const dispatch = useDispatch()
    const webcamRef = useRef<Webcam | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const [chunks, setChunks] = useState<Blob[]>([])
    const [granted, setGranted] = useState<boolean | null>(null)
    const [recording, setRecording] = useState(false)
    const [recorded, setRecorded] = useState(false)
    const mimeType = useMemo(recorderMimeType, [])

    const checkMediaAccess = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
            stream.getTracks().forEach((track) => track.stop())
            setGranted(true)
        } catch {
            setGranted(false)
        }
    }, [])

    useEffect(() => {
        void checkMediaAccess()
    }, [checkMediaAccess])

    useEffect(() => {
        if (granted !== true) return
        const timer = setTimeout(() => {
            const stream = webcamRef.current?.stream
            if (!stream) return
            setRecording(true)
            const recorder = new MediaRecorder(stream, { mimeType })
            recorder.addEventListener("dataavailable", (event: BlobEvent) => {
                if (event.data.size > 0) setChunks((previous) => previous.concat(event.data))
            })
            mediaRecorderRef.current = recorder
            recorder.start()
        }, RECORDING_START_DELAY_MS.web)
        return () => clearTimeout(timer)
        // Un départ, une fois l'accès donné.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [granted])

    // La fin du temps : l'arrêt, puis la vidéo faite de ses morceaux (ils arrivent après l'arrêt). La
    // garde est la vidéo de CET enregistrement, jamais celle que le store garde d'un envoi d'avant
    // (sinon l'enregistrement ne s'arrêterait plus).
    useEffect(() => {
        if (secondsLeft >= 1 || recorded || mediaRecorderRef.current == null) return
        const recorder = mediaRecorderRef.current
        if (recorder.state === "recording" || recorder.state === "paused") recorder.stop()
        setRecording(false)
        if (chunks.length > 0) {
            const blob = new Blob(chunks, { type: mimeType })
            dispatch(setVideoRecord({ uri: URL.createObjectURL(blob) }) as never)
            setChunks([])
            setRecorded(true)
        }
        // Relu à chaque morceau arrivé après l'arrêt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft, chunks])

    // Le viseur ne suit pas le décompte.
    const viewfinder = useMemo(
        () => (
            <View style={{ flex: 1, justifyContent: "center", overflow: "hidden" }}>
                <Webcam
                    audio
                    ref={webcamRef}
                    mirrored
                    muted
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            </View>
        ),
        []
    )

    return {
        phase: recorderPhase({ granted, recording }),
        viewfinder,
        allow: () => void checkMediaAccess(),
        recorded,
    }
}
